import { useQuery } from '@tanstack/react-query';
import { Alert, Button, Drawer, Segmented, Select, Skeleton, Table, Tag, type TableColumnsType } from 'antd';
import type { PersonalOutputFact, PersonalOutputProject, PersonalOutputQuery } from '@youkuai/contracts';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { useAuth } from './auth.tsx';
import { Icon } from './icons.tsx';
import { getPersonalOutput, getPersonalOutputProject } from './operations-v2-api.ts';

type Period = 'WEEK' | 'MONTH' | 'ALL' | 'CUSTOM';
type FactView = 'CONTENT' | 'TASK';

const CONTENT_LABELS = { VIDEO: '视频', ARTICLE: '文章' } as const;
const ROLE_LABELS = { PRIMARY: '主执行', COLLABORATOR: '协作' } as const;
const STATUS_LABELS: Record<string, string> = {
  PLACEHOLDER: '待策划', PLANNED: '已规划', TIERED: '已定档', IN_PROGRESS: '制作中',
  PENDING_DELIVERY: '待交付', DELIVERED: '已交付', VOID: '已作废',
};

function shanghaiToday(): string {
  return new Intl.DateTimeFormat('sv-SE', { timeZone: 'Asia/Shanghai', year: 'numeric', month: '2-digit',
    day: '2-digit' }).format(new Date());
}
function rangeFor(period: Exclude<Period, 'CUSTOM'>): { from: string; until: string } {
  const today = shanghaiToday();
  if (period === 'ALL') return { from: '1900-01-01', until: today };
  if (period === 'MONTH') return { from: `${today.slice(0, 7)}-01`, until: today };
  const current = new Date(`${today}T00:00:00+08:00`);
  const day = current.getDay() || 7;
  current.setDate(current.getDate() - day + 1);
  return { from: new Intl.DateTimeFormat('sv-SE', { timeZone: 'Asia/Shanghai', year: 'numeric', month: '2-digit',
    day: '2-digit' }).format(current), until: today };
}
function periodLabel(period: Period, range: { from: string; until: string }): string {
  if (period === 'ALL') return '全量周期';
  if (period === 'MONTH') return '本月';
  if (period === 'WEEK') return '本周';
  return `${range.from.replaceAll('-', '.')} — ${range.until.replaceAll('-', '.')}`;
}
function formatDate(value: string | null): string {
  if (!value) return '暂无';
  return value.slice(5).replace('-', '/');
}
function errorText(error: unknown): string {
  return error instanceof Error ? error.message : '个人产出读取失败';
}
function uniqueCount<T>(rows: T[], key: (row: T) => string): number {
  return new Set(rows.map(key)).size;
}
function factTime(fact: PersonalOutputFact): string {
  return fact.occurredOn;
}
function basisLabel(fact: PersonalOutputFact): string {
  return fact.periodBasis === 'DELIVERY' ? '交付' : '发布';
}
export function WorkspaceViewTabs({ active = 'OUTPUT' }: { active?: 'PROGRESS' | 'OUTPUT' }) {
  return <nav className="personal-output-tabs" aria-label="工作台视图">
    <Link className={active === 'PROGRESS' ? 'is-active' : ''} to="/workspace">内容进度</Link>
    <Link className={active === 'OUTPUT' ? 'is-active' : ''} to="/workspace/output">个人产出</Link>
  </nav>;
}

function TaskMix({ project }: { project: PersonalOutputProject }) {
  const total = Math.max(1, project.completedTaskCount);
  return <span className="personal-task-mix"><i>{project.taskTypes.slice(0, 4).map((task) =>
    <span key={task.taskCode} style={{ width: `${task.count / total * 100}%` }} />)}</i><small>
      {project.taskTypes.slice(0, 3).map((task) => <span key={task.taskCode}><b>{task.taskName}</b> {task.count}</span>)}
      {project.taskTypes.length > 3 && <span>另 {project.taskTypes.length - 3} 类</span>}
    </small></span>;
}

function ProjectCell({ project }: { project: PersonalOutputProject }) {
  return <span className="personal-project-name"><i>{project.projectName.slice(0, 1)}</i><span>
    <b>{project.projectName}</b><small>{project.businessEntityName}</small></span></span>;
}

function ContentFactTable({ facts }: { facts: PersonalOutputFact[] }) {
  const grouped = new Map<string, { fact: PersonalOutputFact; tasks: string[]; roles: PersonalOutputFact['assignmentRole'][] }>();
  for (const fact of facts) {
    const current = grouped.get(fact.contentId) ?? { fact, tasks: [], roles: [] };
    current.tasks.push(fact.taskName); current.roles.push(fact.assignmentRole);
    if (factTime(fact) > factTime(current.fact)) current.fact = fact;
    grouped.set(fact.contentId, current);
  }
  const rows = [...grouped.values()].sort((left, right) => factTime(right.fact).localeCompare(factTime(left.fact)));
  return <Table className="personal-facts-table" rowKey={(row) => row.fact.contentId} pagination={false} size="small"
    scroll={{ x: 650 }} dataSource={rows} columns={[
      { title: '产出日期', width: 120, render: (_, row) => <span className="personal-date">
        {basisLabel(row.fact)} {formatDate(row.fact.occurredOn)}</span> },
      { title: '内容', width: 250, render: (_, row) => <span className="personal-content-cell"><b>{row.fact.contentTitle ?? '未命名内容'}</b>
        <small>{row.fact.contentCode} · {CONTENT_LABELS[row.fact.contentType]}</small></span> },
      { title: '本人参与任务', width: 200, render: (_, row) => <span className="personal-task-tags">
        {[...new Set(row.tasks)].map((task) => <Tag key={task}>{task}</Tag>)}</span> },
      { title: '当前状态', width: 100, render: (_, row) => <Tag color={row.fact.deliveredInPeriod ? 'success' : 'processing'}>
        {STATUS_LABELS[row.fact.contentStatus] ?? row.fact.contentStatus}</Tag> },
      { title: '', width: 90, render: (_, row) => <Link to={`/projects/${row.fact.projectId}/content?content=${row.fact.contentId}`}>
        打开内容 <Icon name="arrowUpRight" size={14} /></Link> },
    ]} />;
}

function TaskFactTable({ facts }: { facts: PersonalOutputFact[] }) {
  return <Table className="personal-facts-table" rowKey="factId" pagination={false} size="small" scroll={{ x: 720 }}
    dataSource={[...facts].sort((left, right) => factTime(right).localeCompare(factTime(left)))} columns={[
      { title: '归属日期', width: 130, render: (_, row) => <span className="personal-date">
        {basisLabel(row)} {formatDate(row.occurredOn)}</span> },
      { title: '内容', width: 260, render: (_, row) => <span className="personal-content-cell"><b>{row.contentTitle ?? '未命名内容'}</b>
        <small>{row.contentCode} · {CONTENT_LABELS[row.contentType]}</small></span> },
      { title: '任务类型', dataIndex: 'taskName', width: 140, render: (value: string) => <b>{value}</b> },
      { title: '参与方式', dataIndex: 'assignmentRole', width: 110,
        render: (value: PersonalOutputFact['assignmentRole']) => <Tag color={value === 'PRIMARY' ? 'blue' : 'cyan'}>{ROLE_LABELS[value]}</Tag> },
      { title: '当前状态', width: 100, render: (_, row) => <Tag color={row.deliveredInPeriod ? 'success' : 'processing'}>
        {STATUS_LABELS[row.contentStatus] ?? row.contentStatus}</Tag> },
    ]} />;
}

export function PersonalOutputPage() {
  const auth = useAuth();
  const session = auth.state.kind === 'ready' ? auth.state : null;
  const initial = rangeFor('MONTH');
  const [period, setPeriod] = useState<Period>('MONTH');
  const [range, setRange] = useState(initial);
  const [contentType, setContentType] = useState<PersonalOutputQuery['contentType']>(null);
  const [taskCode, setTaskCode] = useState<string | null>(null);
  const [assignmentRole, setAssignmentRole] = useState<PersonalOutputQuery['assignmentRole']>(null);
  const [projectId, setProjectId] = useState<string | null>(null);
  const [selectedProject, setSelectedProject] = useState<PersonalOutputProject | null>(null);
  const [drawerTask, setDrawerTask] = useState<string | null>(null);
  const [factView, setFactView] = useState<FactView>('CONTENT');
  const filter = useMemo<PersonalOutputQuery>(() => ({ from: range.from, until: range.until,
    contentType, taskCode, assignmentRole }), [range, contentType, taskCode, assignmentRole]);
  const dashboard = useQuery({ queryKey: ['personal-output', session?.session.userId, filter],
    queryFn: () => getPersonalOutput(session!.token, filter), enabled: !!session, staleTime: 30_000 });
  const detail = useQuery({ queryKey: ['personal-output-project', session?.session.userId, selectedProject?.projectId, filter],
    queryFn: () => getPersonalOutputProject(session!.token, selectedProject!.projectId, filter),
    enabled: !!session && !!selectedProject, staleTime: 30_000 });
  const projects = (dashboard.data?.projects ?? []).filter((project) => !projectId || project.projectId === projectId);
  const totals = projects.reduce((result, project) => ({ projects: result.projects + 1,
    contents: result.contents + project.contentCount, tasks: result.tasks + project.completedTaskCount,
    delivered: result.delivered + project.deliveredContentCount, primary: result.primary + project.primaryCount,
    collaborator: result.collaborator + project.collaboratorCount }),
  { projects: 0, contents: 0, tasks: 0, delivered: 0, primary: 0, collaborator: 0 });
  const drawerFacts = (detail.data?.facts ?? []).filter((fact) => !drawerTask || fact.taskCode === drawerTask);
  const drawerLastOccurredOn = (detail.data?.facts ?? []).reduce<string | null>((latest, fact) =>
    !latest || fact.occurredOn > latest ? fact.occurredOn : latest, null);
  const drawerContentTotal = uniqueCount(detail.data?.facts ?? [], (fact) => fact.contentId);
  const drawerTaskTotal = detail.data?.facts.length ?? 0;
  const drawerVideo = uniqueCount((detail.data?.facts ?? []).filter((fact) => fact.contentType === 'VIDEO'), (fact) => fact.contentId);
  const drawerArticle = uniqueCount((detail.data?.facts ?? []).filter((fact) => fact.contentType === 'ARTICLE'), (fact) => fact.contentId);
  const drawerTaskTypes = [...new Map((detail.data?.facts ?? []).map((fact) => [fact.taskCode,
    { taskCode: fact.taskCode, taskName: fact.taskName, count: 0, contents: new Set<string>(), primary: 0, collaborator: 0 }])).values()];
  for (const fact of detail.data?.facts ?? []) {
    const row = drawerTaskTypes.find((item) => item.taskCode === fact.taskCode)!;
    row.count += 1; row.contents.add(fact.contentId);
    if (fact.assignmentRole === 'PRIMARY') row.primary += 1; else row.collaborator += 1;
  }
  drawerTaskTypes.sort((left, right) => right.count - left.count || left.taskName.localeCompare(right.taskName, 'zh-CN'));

  function changePeriod(next: Period) {
    setPeriod(next);
    if (next !== 'CUSTOM') setRange(rangeFor(next));
  }
  function clearFilters() {
    setContentType(null); setTaskCode(null); setAssignmentRole(null); setProjectId(null);
  }
  function openProject(project: PersonalOutputProject) {
    setSelectedProject(project); setDrawerTask(null); setFactView('CONTENT');
  }

  const columns: TableColumnsType<PersonalOutputProject> = [
    { title: '项目', key: 'project', width: 240, render: (_, row) => <ProjectCell project={row} /> },
    { title: '内容产出', key: 'content', width: 150, render: (_, row) => <span className="personal-output-number">
      <b>{row.contentCount}</b><small>{row.contentTypes.map((item) => `${CONTENT_LABELS[item.contentType]} ${item.count}`).join(' · ')}</small></span> },
    { title: '产出任务', dataIndex: 'completedTaskCount', width: 110,
      render: (value: number) => <b className="personal-strong-number">{value}</b> },
    { title: '任务构成', key: 'taskMix', width: 330, render: (_, row) => <TaskMix project={row} /> },
    { title: '周期交付', dataIndex: 'deliveredContentCount', width: 110,
      render: (value: number) => <b className="personal-delivered-number">{value}</b> },
    { title: '最近产出', dataIndex: 'lastOccurredOn', width: 130,
      render: (value: string | null) => <span className="personal-date">{formatDate(value)}</span> },
    { title: '', key: 'action', width: 90, fixed: 'right', render: (_, row) => <Button size="small"
      onClick={(event) => { event.stopPropagation(); openProject(row); }}>查看</Button> },
  ];

  return <main className="page-shell personal-output-page">
    <WorkspaceViewTabs />
    <header className="personal-output-hero">
      <div><span>PERSONAL OUTPUT LEDGER</span><h1>个人产出</h1>
        <p>按项目理解工作，再下钻到任务类型与具体内容。生产按实际交付日、发布按实际发布日期归期。</p>
        <span className="personal-output-period"><Icon name="calendar" size={14} />当前周期 · {periodLabel(period, range)}</span></div>
      <div aria-hidden="true"><img src="/management-dashboard/owl-ops-hero.webp" width="512" height="512" alt="" />
        <span><b>我的产出助手</b><small>按交付与发布统计</small></span></div>
    </header>

    <section className="personal-output-filters"><header><span><Icon name="filter" size={16} /></span><div>
      <b>产出筛选</b><small>筛选后，指标、项目和下钻明细同步更新</small></div></header><div>
      <label><span>周期</span><Segmented size="small" value={period} onChange={(value) => changePeriod(value as Period)}
        options={[{ label: '本周', value: 'WEEK' }, { label: '本月', value: 'MONTH' },
          { label: '全量', value: 'ALL' }, { label: '自选', value: 'CUSTOM' }]} /></label>
      {period === 'CUSTOM' && <label className="personal-custom-range"><span>日期范围</span><i><input type="date" value={range.from}
        max={range.until} onChange={(event) => { if (event.target.value) setRange((current) =>
          ({ ...current, from: event.target.value })); }} />
        <em>至</em><input type="date" value={range.until} min={range.from}
          onChange={(event) => { if (event.target.value) setRange((current) =>
            ({ ...current, until: event.target.value })); }} /></i></label>}
      <label><span>内容类型</span><Select allowClear value={contentType} placeholder="全部类型"
        onChange={(value) => setContentType(value ?? null)} options={Object.entries(CONTENT_LABELS).map(([value, label]) => ({ value, label }))} /></label>
      <label><span>项目</span><Select allowClear showSearch optionFilterProp="label" value={projectId} placeholder="全部项目"
        onChange={(value) => setProjectId(value ?? null)} options={(dashboard.data?.projectOptions ?? []).map((project) =>
          ({ value: project.projectId, label: project.projectName }))} /></label>
      <label><span>任务类型</span><Select allowClear showSearch optionFilterProp="label" value={taskCode} placeholder="全部任务"
        onChange={(value) => setTaskCode(value ?? null)} options={(dashboard.data?.taskOptions ?? []).map((task) =>
          ({ value: task.taskCode, label: task.taskName }))} /></label>
      <label><span>参与方式</span><Select allowClear value={assignmentRole} placeholder="全部方式"
        onChange={(value) => setAssignmentRole(value ?? null)} options={Object.entries(ROLE_LABELS).map(([value, label]) => ({ value, label }))} /></label>
      <button type="button" onClick={clearFilters}>清除筛选</button>
    </div></section>

    {dashboard.isError && <Alert type="error" showIcon title="个人产出读取失败" description={errorText(dashboard.error)}
      action={<Button onClick={() => void dashboard.refetch()}>重试</Button>} />}
    {dashboard.isPending && <Skeleton active paragraph={{ rows: 10 }} />}
    {dashboard.data && <>
      <section className="personal-output-metrics">
        {[[ '参与项目', totals.projects, '周期内有实际交付或发布' ],
          [ '产出内容', totals.contents, '本人参与内容，按内容去重' ], [ '产出任务', totals.tasks, '按交付或发布事实统计' ],
          [ '周期内交付', totals.delivered, '本人参与生产的内容' ],
          [ '主执行 / 协作', `${totals.primary} / ${totals.collaborator}`, '按产出时有效分配关系' ]]
          .map(([label, value, hint]) => <article key={label}><small>{label}</small><strong>{value}</strong><span>{hint}</span></article>)}
      </section>
      <section className="personal-output-deck"><header><div><span>PROJECT OUTPUT</span><h2>按项目查看产出</h2>
        <small>点击项目，查看实际交付与发布对应的参与任务</small></div><b>{projects.length} 个项目</b></header>
        <Table rowKey="projectId" dataSource={projects} columns={columns} pagination={false} scroll={{ x: 1180 }}
          locale={{ emptyText: '当前筛选条件下没有个人产出记录' }}
          onRow={(row) => ({ onClick: () => openProject(row), className: 'personal-output-project-row' })} />
      </section>
      <p className="personal-output-note">生产任务按实际交付日归期，发布任务按实际发布日期归期；分派和完成时间只作审计。</p>
    </>}

    <Drawer rootClassName="personal-output-drawer" size={900} open={!!selectedProject}
      onClose={() => setSelectedProject(null)} title={<span><b>{selectedProject?.projectName} · 个人产出</b>
        <small>{periodLabel(period, range)} · 交付与发布产出事实</small></span>}>
      {detail.isError && <Alert type="error" showIcon title="项目产出读取失败" description={errorText(detail.error)}
        action={<Button onClick={() => void detail.refetch()}>重试</Button>} />}
      {detail.isPending && <Skeleton active paragraph={{ rows: 12 }} />}
      {detail.data && <div className="personal-output-drawer-body">
        <section className="personal-drawer-metrics"><div><small>内容产出</small><b>{drawerContentTotal}</b></div>
          <div><small>产出任务</small><b>{drawerTaskTotal}</b></div>
          <div><small>周期内交付</small><b>{uniqueCount(detail.data.facts.filter((fact) => fact.deliveredInPeriod), (fact) => fact.contentId)}</b></div>
          <div><small>最近产出</small><b>{formatDate(drawerLastOccurredOn)}</b></div></section>
        <section className="personal-drawer-card"><header><div><h3>内容类型</h3><small>说明生产对象，不代表任务价值</small></div></header><div className="personal-content-split">
          {([['VIDEO', drawerVideo], ['ARTICLE', drawerArticle]] as const).map(([type, count]) => <article key={type} className={type === 'ARTICLE' ? 'is-article' : ''}>
            <header><b>{CONTENT_LABELS[type]}内容</b><strong>{count}</strong></header><i><span style={{ width: `${count / Math.max(1, drawerContentTotal) * 100}%` }} /></i>
            <footer><span>{Math.round(count / Math.max(1, drawerContentTotal) * 100)}%</span><span>按内容去重</span></footer></article>)}</div></section>
        <section className="personal-drawer-card"><header><div><h3>任务类型分布</h3><small>点击类型筛选下方事实；再次点击取消</small></div><Button type="text" onClick={() => setDrawerTask(null)}>全部任务</Button></header>
          <div className="personal-task-types">{drawerTaskTypes.map((task) => <button type="button" key={task.taskCode}
            className={drawerTask === task.taskCode ? 'is-active' : ''}
            onClick={() => setDrawerTask((current) => current === task.taskCode ? null : task.taskCode)}>
            <b>{task.taskName}</b><i><span style={{ width: `${task.count / Math.max(1, drawerTaskTypes[0]?.count ?? 1) * 100}%` }} /></i>
            <span><strong>{task.count}</strong> 项</span><span>{task.contents.size} 条内容</span><span>主 {task.primary} · 协 {task.collaborator}</span></button>)}</div>
        </section>
        <section className="personal-drawer-card personal-facts-card"><header><div><h3>产出归属记录</h3>
          <small>{drawerTask ? drawerTaskTypes.find((task) => task.taskCode === drawerTask)?.taskName : '全部任务'} · {drawerFacts.length} 项</small></div>
          <Segmented size="small" value={factView} onChange={(value) => setFactView(value as FactView)}
            options={[{ label: '内容视角', value: 'CONTENT' }, { label: '任务明细', value: 'TASK' }]} /></header>
          {factView === 'CONTENT' ? <ContentFactTable facts={drawerFacts} /> : <TaskFactTable facts={drawerFacts} />}</section>
        <div className="personal-output-fee-note"><Icon name="info" size={16} /><span><b>费用口径</b>本页不根据任务数量推算收入。后续如展示金额，必须读取任务锁定金额与本人份额，并复用个人结算权限。</span></div>
      </div>}
    </Drawer>
  </main>;
}
